import { useCallback, useEffect, useState } from 'react';
import { Link } from '../components/Link.tsx';
import { PageHead, Section } from '../components/common.tsx';
import { fmtTime, useCtx } from '../lib.ts';

type State = 'ok' | 'warn' | 'bad' | 'info';
interface Check {
  id: string;
  label: string;
  state: State;
  value: string;
  detail: string;
}
interface SystemView {
  at: string;
  overall: State;
  groups: { title: string; checks: Check[] }[];
  readiness: { mode: string; devRoutes: boolean; findings: { severity: 'ERROR' | 'WARN' | 'INFO'; key: string; message: string }[] };
}

const MARK: Record<State, string> = { ok: '✓', warn: '!', bad: '✕', info: 'i' };
const HEADLINE: Record<State, [string, string]> = {
  ok: ['All systems working', 'Every service answers and nothing is backed up.'],
  warn: ['Working, with warnings', 'Something needs attention soon — see the yellow items.'],
  bad: ['Problem detected', 'At least one part is down or unsafe — see the red items first.'],
  info: ['', ''],
};

/**
 * Platform health in plain terms (API mode): services, game, money safety, background work, server and
 * data — each measured now, with what it means. Refreshes every 30 s. The camera pipeline has its own page.
 */
export function System() {
  const { backend, data } = useCtx();
  const server = backend.server;
  const [v, setV] = useState<SystemView | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const load = useCallback(async () => {
    if (!server) return;
    const r = await server.get<SystemView>('/v1/admin/system');
    if (r.ok) {
      setV(r.value);
      setErr(null);
    } else setErr(r.error);
  }, [server]);
  useEffect(() => {
    if (!server) return;
    const t0 = setTimeout(() => void load(), 0);
    const t = setInterval(() => void load(), 30_000);
    return () => {
      clearTimeout(t0);
      clearInterval(t);
    };
  }, [server, load]);

  if (!server)
    return (
      <>
        <PageHead title="System">Live platform health needs the UrbanRats backend.</PageHead>
        <p className="empty" data-testid="system">
          This is the demo: data comes from simulations in this browser tab ({data.rounds.length} rounds, {data.bets.length} bets). Nothing here measures real infrastructure.
        </p>
      </>
    );
  if (err && !v)
    return (
      <p role="alert" className="field-error">
        {err}
      </p>
    );
  if (!v) return <p aria-busy="true">Checking the platform…</p>;
  const [title, sub] = HEADLINE[v.overall];
  const errors = v.readiness.findings.filter((f) => f.severity === 'ERROR');
  const warns = v.readiness.findings.filter((f) => f.severity !== 'ERROR');
  return (
    <>
      <PageHead title="System">
        Is the platform healthy right now? Each line is measured live and says what it means. Updated every 30 s (last {fmtTime(v.at)}). Cameras in detail: <Link to="/camera-pipeline">Camera pipeline</Link>.
      </PageHead>
      <div className={`sy-head sy-${v.overall}`} data-testid="system">
        <span className="sy-big" aria-hidden>
          {MARK[v.overall]}
        </span>
        <div>
          <b>{title}</b>
          <span>{sub}</span>
        </div>
        <button type="button" className="btn ghost" onClick={() => void load()}>
          Refresh
        </button>
      </div>
      <div className="sy-groups">
        {v.groups.map((g) => (
          <Section key={g.title} title={g.title}>
            <ul className="sy-list">
              {g.checks.map((c) => (
                <li key={c.id} className={`sy-row sy-${c.state}`}>
                  <span className="sy-mark" aria-label={c.state}>
                    {MARK[c.state]}
                  </span>
                  <div>
                    <div className="sy-line">
                      <strong>{c.label}</strong>
                      <span className="sy-val">{c.value}</span>
                    </div>
                    {c.detail && <p>{c.detail}</p>}
                  </div>
                </li>
              ))}
            </ul>
          </Section>
        ))}
      </div>
      <Section title="Ready for production?">
        <p className="note">
          Running in <strong>{v.readiness.mode}</strong> mode{v.readiness.devRoutes ? ' with developer test routes switched on' : ''}. In production mode the servers refuse to start while any item below is red. These are the open items before real-money operation.
        </p>
        {errors.length + warns.length === 0 ? (
          <p className="ok-text">No open items.</p>
        ) : (
          <ul className="sy-list">
            {[...errors, ...warns].map((f) => (
              <li key={f.key + f.message} className={`sy-row sy-${f.severity === 'ERROR' ? 'bad' : 'warn'}`}>
                <span className="sy-mark">{f.severity === 'ERROR' ? '✕' : '!'}</span>
                <div>
                  <div className="sy-line">
                    <strong>{f.message}</strong>
                  </div>
                  <p className="mono">{f.key}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>
      <Section title="When something is red">
        <p className="note">Step-by-step procedures are in the runbooks (docs/release/RUNBOOKS.md): restart only between rounds (safe-restart), never relax camera rules to make a round settle, and never re-send an unknown wallet call — resolve it from Reconciliation.</p>
      </Section>
    </>
  );
}
